/**
 * Which model a hand has taken an interest in, and for how much longer it says so.
 *
 * The box round a model is a statement, and a statement made continuously is one nobody
 * reads. A hand at rest points wherever it happens to point, so an outline drawn for as
 * long as the beam crossed something would be an outline that was up almost all the time —
 * decoration rather than an answer to a question. So it is asked for, and it lapses.
 *
 * Nothing here knows what a model is beyond its identity, or where the time comes from:
 * frames are handed in as a number of seconds, like everything else in `domain`.
 */

import type { ModelId } from './types.js';

/**
 * How long a selection outlives whatever last renewed it, in seconds.
 *
 * Long enough to look at the model you have just picked out and then reach for the stick,
 * short enough that a box still standing round something is a box you put there.
 */
export const SELECTION_LINGER_S = 3;

export class Selection {
  private chosen: ModelId | undefined;
  private remaining = 0;

  /** The model this hand currently means, or nothing if it has stopped meaning any. */
  get model(): ModelId | undefined {
    return this.chosen;
  }

  /**
   * Take an interest in a model, restarting the clock.
   *
   * Renewing is the same call as choosing afresh: a hand that goes on driving a model is
   * making the same statement about it every frame, and the box should not lapse under a
   * stick that is still being pushed.
   */
  choose(id: ModelId): void {
    this.chosen = id;
    this.remaining = SELECTION_LINGER_S;
  }

  /** Let a frame's worth of time pass, dropping the selection once it has run out. */
  tick(seconds: number): void {
    if (this.chosen === undefined) return;

    this.remaining -= seconds;
    if (this.remaining <= 0) this.clear();
  }

  clear(): void {
    this.chosen = undefined;
    this.remaining = 0;
  }
}
